import * as React from 'react';

import { MultiSelectFilter } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { FilterLabel } from '../../_shared/FilterLabels';

import { FacilityRecord } from '~/modules/finance/_graphql/queryFinanceFacilities';
import {
  $columnSelectedFacilityIds,
  $facilityFilterSearchTerm,
  $remoteFilterFacilities,
} from '~/modules/finance/_state/financeBills.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface BillFacilityColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ BILL FACILITY COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const BillFacilityColumnFilter: React.FC<
  BillFacilityColumnFilterProps
> = ({ onClose }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const {
    data: facilitiesData,
    isFetching: isFetchingFacilities,
    isLoading: isLoadingFacilities,
  } = useAtomValue($remoteFilterFacilities);
  const [selectedFacilityIdsSet, setSelectedFacilityIdsSet] = useAtom(
    $columnSelectedFacilityIds,
  );
  const setSearchTerm = useSetAtom($facilityFilterSearchTerm);

  // Memoized values
  const selectedFacilityIds = React.useMemo(
    () => Array.from(selectedFacilityIdsSet),
    [selectedFacilityIdsSet],
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedFacilityIds),
    [selectedFacilityIds],
  );

  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData],
  );

  const selectedFacilities = React.useMemo(
    () =>
      facilities.filter((facility) =>
        selectedFacilityIds.includes(facility.id),
      ),
    [facilities, selectedFacilityIds],
  );

  // Callbacks
  const handleSearch = React.useCallback(
    (query: string) => {
      setSearchTerm(query || undefined);
    },
    [setSearchTerm],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedFacilityIdsSet(new Set(Array.from(keys) as string[]));
    },
    [setSelectedFacilityIdsSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedFacilityIdsSet(new Set());
  }, [setSelectedFacilityIdsSet]);

  const getLabel = React.useCallback(
    (item: FacilityRecord, isSelected: boolean) => (
      <FilterLabel
        label={item.facilityName}
        address={item.address}
        isSelected={isSelected}
      />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@BillFacilityColumnFilter min-w-[350px]"
      entries={facilities}
      selectedEntries={selectedFacilities}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(facility) => facility.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.bills.list.search.facilityPlaceholder}
      isLoading={isLoadingFacilities}
      isFetching={isFetchingFacilities}
    />
  );
};
